import { ReactNode } from "react";
import {
  A4_HEIGHT_PX,
  A4_WIDTH_PX,
  PDF_COLORS,
  PDF_SAFE_ZONE,
} from "./pdfConstants";
import VitalBg from "assets/img/VitalBg.png";
import VitalBgDark from "assets/img/vitalBgDark.png";
import VitalFirstBg from "assets/img/VitalFirstPage.png";

interface PdfPageShellProps {
  children?: ReactNode;
  pageNumber?: number;
  totalPages?: number;
  backgroundImage?: string;
  bleed?: boolean;
  title?: string;
  dir?: "rtl" | "ltr";
  dark?: boolean;
}

export default function PdfPageShell({
  children,
  pageNumber,
  totalPages,
  backgroundImage,
  bleed = false,
  title,
  dir = "rtl",
  dark = false,
}: PdfPageShellProps) {
  const bg = bleed ? VitalFirstBg : backgroundImage || VitalBg;

  return (
    <div
      data-pdf-page
      dir={dir}
      style={{
        width: A4_WIDTH_PX,
        height: A4_HEIGHT_PX,
        backgroundColor: dark ? "#050B1A" : PDF_COLORS.paper,
        color: dark ? "#FFFFFF" : PDF_COLORS.title,
        position: "relative",
        overflow: "hidden",
        fontFamily:
          "IranYekan, Vazirmatn, Tahoma, 'Segoe UI', Arial, sans-serif",
        boxSizing: "border-box",
      }}
    >
      {dark && !bleed ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${VitalBgDark}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: 0,
          }}
        />
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url("${bg}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: 0,
          }}
        />
      )}

      {/* ============ Content ============ */}
      {bleed ? (
        <div style={{ position: "absolute", inset: 0, zIndex: 2 }}>
          {children}
        </div>
      ) : (
        <>
          {title ? (
            <div
              style={{
                position: "absolute",
                top: 18,
                [dir === "rtl" ? "right" : "left"]: PDF_SAFE_ZONE.side + 8,
                zIndex: 3,
                maxWidth: "70%",
              }}
            >
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#FFFFFF",
                  letterSpacing: 0.3,
                  textAlign: dir === "rtl" ? "right" : "left",
                  textShadow: "0 2px 8px rgba(0,0,0,0.7)",
                }}
              >
                {title}
              </h1>
              <div
                style={{
                  marginTop: 6,
                  height: 3,
                  width: 60,
                  borderRadius: 2,
                  background: `linear-gradient(${
                    dir === "rtl" ? "to left" : "to right"
                  }, #FFFFFF, rgba(255,255,255,0))`,
                  marginInlineStart: dir === "rtl" ? "auto" : 0,
                  marginInlineEnd: dir === "rtl" ? 0 : "auto",
                }}
              />
            </div>
          ) : null}

          <div
            style={{
              position: "absolute",
              top: PDF_SAFE_ZONE.top,
              left: PDF_SAFE_ZONE.side,
              right: PDF_SAFE_ZONE.side,
              bottom: PDF_SAFE_ZONE.bottom,
              zIndex: 2,
              overflow: "hidden",
              color: dark ? "#FFFFFF" : PDF_COLORS.title,
            }}
          >
            {children}
          </div>
        </>
      )}

      {!bleed && pageNumber != null && totalPages != null ? (
        <div
          style={{
            position: "absolute",
            bottom: PDF_SAFE_ZONE.pageNumberBottom,
            left: PDF_SAFE_ZONE.pageNumberLeft,
            zIndex: 3,
            color: dark ? "rgba(255,255,255,0.9)" : PDF_COLORS.muted,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          {pageNumber} / {totalPages}
        </div>
      ) : null}
    </div>
  );
}
